import type { PreloadedState } from "@reduxjs/toolkit";
import { combineReducers, configureStore } from "@reduxjs/toolkit";
// Attorneys
import { apiAttorneysSlice } from "services/attorneys/attorneysApi";
// Employees
import { apiEmployeesSlice } from "services/employees/employeesApi";
// Filters
import { apiFiltersSlice } from "services/filters/filtersApi";
import filtersReducer from "services/filters/filtersSlice";
// Firm
import { apiFirmSlice } from "services/firm/firmApi";
// Impersonate
import { apiImpersonateSlice } from "services/impersonate/impersonateApi";
import impersonateReducer from "services/impersonate/impersonateSlice";
// Job
import { apiJobSlice } from "services/job/jobApi";
import jobReducer from "services/job/jobSlice";
// Jobs
import { apiJobsSlice } from "services/jobs/jobsApi";
// Notifications
import notificationsReducer from "services/notifications/notificationsSlice";
// Permissions
import permissionsReducer from "services/permissions/permissionsSlice";
// Public v2
import { apiPublicSlice as apiPublicV2Slice } from "services/v2/public/publicApi";
// Deals
import { apiDealsSlice } from "services/deals/dealsApi";
// Litigation
import { apiLitigationSlice } from "services/litigation/litigationApi";
// Litigation v2
import { apiLitigationV2Slice } from "services/v2/litigation/litigationApi";
// Deals v2
import { apiDealsV2Slice } from "services/v2/deals/dealsApi";
// User
import { apiReleaseNotesSlice } from "services/release-notes/releaseNotesApi";
import userReducer from "services/user/userSlice";
import usersReducer from "services/users/usersSlice";

// v2
// Practice Areas
import { practiceAreaApi } from "services/v2/practice-areas/practiceAreaApi";
// Specialties
import { specialtyApi } from "services/v2/specialties/specialtyApi";
// Industries
import { industryApi } from "services/v2/industries/industryApi";
// Practice Types
import { practiceTypeApi } from "services/v2/practice-types/practiceTypeApi";
// Titles
import { titleApi } from "services/v2/titles/titleApi";
// Offices
import { officeApi } from "services/v2/offices/officeApi";
// Schools
import { schoolApi } from "services/v2/schools/schoolApi";
// cities
import { cityApi } from "services/v2/cities/cityApi";
// Employees
import { employeeApi } from "services/v2/employees/employeeApi";
// Admissions
import { admissionApi } from "services/v2/admissions/admissionApi";
// Honors
import { honorApi } from "services/v2/honors/honorApi";
// Employers
import { employerApi } from "services/v2/employers/employerApi";
// Languages
import { languageApi } from "services/v2/languages/languageApi";
// Attorneys
import { attorneyApi } from "services/v2/attorneys/attorneyApi";
// Employer Analytics
import { apiEmployerAnaliticsSlice } from "services/v2/employer-analitics/employerAnaliticsApi";
// Jobs
import { jobApi } from "services/v2/jobs/jobApi";
import v2JobReducer from "services/v2/jobs/jobSlice";
// Candidates
import { candidatesApi } from "services/v2/candidates/candidatesApi";
// Job Attorney Fit
import { candidateManagementApi } from "services/v2/candidates-management/candidateManagementApi";
// Attorney Connections
import { attorneyConnectionApi } from "services/v2/attorney-connections/attorneyConnectionApi";
// Saved Searches
import { savedSearchesApi } from "services/v2/saved-searches/savedSearchesApi";
// Notes
import { attorneyNotesApi } from "services/v2/attorney-notes/attorneyNotesApi";
import { candidateNotesApi } from "services/v2/candidate-notes/candidateNotesApi";
import { jobNotesApi } from "services/v2/job-notes/jobNotesApi";
// Excluded Firms v2
import { excludedFirmsApi } from "services/v2/excluded-firms";
// Attorney Downloads v2
import { attorneyDownloadsApi } from "services/v2/attorney-downloads";

const rootReducer = combineReducers({
  [apiFiltersSlice.reducerPath]: apiFiltersSlice.reducer,
  [apiFirmSlice.reducerPath]: apiFirmSlice.reducer,
  [apiAttorneysSlice.reducerPath]: apiAttorneysSlice.reducer,
  [apiJobsSlice.reducerPath]: apiJobsSlice.reducer,
  [apiJobSlice.reducerPath]: apiJobSlice.reducer,
  [apiEmployeesSlice.reducerPath]: apiEmployeesSlice.reducer,

  [apiPublicV2Slice.reducerPath]: apiPublicV2Slice.reducer,
  [apiImpersonateSlice.reducerPath]: apiImpersonateSlice.reducer,
  [apiReleaseNotesSlice.reducerPath]: apiReleaseNotesSlice.reducer,
  [apiDealsSlice.reducerPath]: apiDealsSlice.reducer,
  [apiLitigationSlice.reducerPath]: apiLitigationSlice.reducer,
  [apiDealsV2Slice.reducerPath]: apiDealsV2Slice.reducer,
  [apiLitigationV2Slice.reducerPath]: apiLitigationV2Slice.reducer,
  [practiceAreaApi.reducerPath]: practiceAreaApi.reducer,
  [specialtyApi.reducerPath]: specialtyApi.reducer,
  [industryApi.reducerPath]: industryApi.reducer,
  [practiceTypeApi.reducerPath]: practiceTypeApi.reducer,
  [titleApi.reducerPath]: titleApi.reducer,
  [officeApi.reducerPath]: officeApi.reducer,
  [schoolApi.reducerPath]: schoolApi.reducer,
  [cityApi.reducerPath]: cityApi.reducer,
  [employeeApi.reducerPath]: employeeApi.reducer,
  [admissionApi.reducerPath]: admissionApi.reducer,
  [honorApi.reducerPath]: honorApi.reducer,
  [employerApi.reducerPath]: employerApi.reducer,
  [languageApi.reducerPath]: languageApi.reducer,
  [attorneyApi.reducerPath]: attorneyApi.reducer,
  [jobApi.reducerPath]: jobApi.reducer,
  [candidatesApi.reducerPath]: candidatesApi.reducer,
  [candidateManagementApi.reducerPath]: candidateManagementApi.reducer,
  [apiEmployerAnaliticsSlice.reducerPath]: apiEmployerAnaliticsSlice.reducer,
  [attorneyConnectionApi.reducerPath]: attorneyConnectionApi.reducer,
  [savedSearchesApi.reducerPath]: savedSearchesApi.reducer,
  [attorneyNotesApi.reducerPath]: attorneyNotesApi.reducer,
  [jobNotesApi.reducerPath]: jobNotesApi.reducer,
  [candidateNotesApi.reducerPath]: candidateNotesApi.reducer,
  [excludedFirmsApi.reducerPath]: excludedFirmsApi.reducer,
  [attorneyDownloadsApi.reducerPath]: attorneyDownloadsApi.reducer,
  users: usersReducer,
  user: userReducer,
  filters: filtersReducer,
  notifications: notificationsReducer,
  job: jobReducer,
  v2Job: v2JobReducer,
  permissions: permissionsReducer,
  impersonate: impersonateReducer
});

const middleware = (getDefaultMiddleware: any) => {
  return [
    // Other middleware that you want to include
    // ...
    ...getDefaultMiddleware({
      serializableCheck: false
    }),
    // Middleware for your API slices
    apiAttorneysSlice.middleware,
    apiFiltersSlice.middleware,
    apiFirmSlice.middleware,
    apiJobsSlice.middleware,
    apiJobSlice.middleware,
    apiEmployeesSlice.middleware,

    apiPublicV2Slice.middleware,
    apiImpersonateSlice.middleware,
    apiReleaseNotesSlice.middleware,
    apiDealsSlice.middleware,
    apiLitigationSlice.middleware,
    apiDealsV2Slice.middleware,
    apiLitigationV2Slice.middleware,
    practiceAreaApi.middleware,
    specialtyApi.middleware,
    industryApi.middleware,
    practiceTypeApi.middleware,
    titleApi.middleware,
    officeApi.middleware,
    schoolApi.middleware,
    cityApi.middleware,
    employeeApi.middleware,
    admissionApi.middleware,
    honorApi.middleware,
    employerApi.middleware,
    languageApi.middleware,
    attorneyApi.middleware,
    jobApi.middleware,
    candidatesApi.middleware,
    candidateManagementApi.middleware,
    apiEmployerAnaliticsSlice.middleware,
    apiPublicV2Slice.middleware,
    attorneyConnectionApi.middleware,
    savedSearchesApi.middleware,
    attorneyNotesApi.middleware,
    jobNotesApi.middleware,
    candidateNotesApi.middleware,
    excludedFirmsApi.middleware,
    attorneyDownloadsApi.middleware
  ];
};

export type RootState = ReturnType<typeof rootReducer>;

export const setupStore = (preloadedState?: PreloadedState<RootState>) =>
  configureStore({
    reducer: rootReducer,
    middleware,
    preloadedState
  });

export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = AppStore["dispatch"];
